import MultipleChoiceQuestion from "@/components/questions/MultipleChoiceQuestion/MultipleChoiceQuestion";
import TransitionContent from "@/components/questions/TransitionContent/TransitionContent";
import TextInput from "@/components/questions/TextInput/TextInput";
import InfoToast from "@/components/InfoToast/InfoToast";
import { fireSkipEvent } from "@/utils/analytics";
import { useVariant, PAGE_VARIANT } from "@/hooks/useVariant";
import {
  QuestionType,
  MultipleChoiceQuestion as MultipleChoiceQuestionType,
  TransitionQuestion,
  TextQuestion,
} from "@/commonTypes/question";
import { AnswerValue } from "@/commonTypes/answer";
import { isConditionMet } from "@/utils/conditionEval";
import {
  getMultipleChoiceAnswer,
  getTextGroupAnswer as getTextAnswer,
  getStringAnswer,
} from "@/utils/answerHelpers";
import DatePickerQuestion from "@/components/questions/DatePickerQuestion/DatePickerQuestion";
import { useAssessment } from "@/contexts/AssessmentContext/AssessmentContext";
import ProgressBar from "@/components/ProgressBar/ProgressBar";
import { NavigationButtons } from "@/components/NavigationButtons/NavigationButtons";
import { useTranslations } from "next-intl";
import {
  type ReactNode,
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import SubmitQuestion from "@/components/questions/SubmitQuestion/SubmitQuestion";
import {
  type UnitSystem,
  UNIT_PREFERENCE_KEY,
} from "@/constants/surveyMetadata";

export function QuestionRenderer() {
  const {
    currentIndex,
    visibleQuestions,
    currentQuestion: question,
    currentAnswer,
    canGoNext,
    canGoBack,
    isLastQuestion,
    goNext,
    goBack,
    goToQuestion,
    setAnswer,
    onValidationChange,
    answers,
    answersRecord,
  } = useAssessment();

  const t = useTranslations();
  const tQuestionnaire = useTranslations("questionnaire");
  const [prevIndex, setPrevIndex] = useState(currentIndex);
  const [slideDirection, setSlideDirection] = useState<"forward" | "back">(
    "forward",
  );
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Snapshot of visibleQuestions that only updates on navigation, so the
  // progress bar total doesn't jump before auto-advance fires.
  const [progressQuestions, setProgressQuestions] = useState(visibleQuestions);

  if (currentIndex !== prevIndex) {
    setSlideDirection(currentIndex > prevIndex ? "forward" : "back");
    setPrevIndex(currentIndex);
    setIsAutoAdvancing(false);
    setIsFadingOut(false);
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
    setProgressQuestions(visibleQuestions);
  }

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
      }
    };
  }, []);

  // Delayed next: 0.125s fade-out, then advance
  const handleNext = useCallback(() => {
    if (prefersReducedMotion) {
      goNext();
      return;
    }
    if (transitionTimerRef.current) return;
    setIsAutoAdvancing(true);
    setIsFadingOut(true);
    transitionTimerRef.current = setTimeout(() => {
      setIsFadingOut(false);
      goNext();
      transitionTimerRef.current = null;
    }, 125);
  }, [goNext]);

  // Exclude non-interactive question types from the progress bar count.
  const answerableQuestions = useMemo(
    () =>
      progressQuestions.filter(
        (q) =>
          q.questionType !== QuestionType.TRANSITION &&
          q.questionType !== QuestionType.SUBMIT,
      ),
    [progressQuestions],
  );

  const answerableTotal = answerableQuestions.length;

  const answerableIndex = question
    ? answerableQuestions.findIndex((q) => q.questionId === question.questionId)
    : 0;

  if (!question) return null;

  const isTransition = question.questionType === QuestionType.TRANSITION;
  const isSubmit = question.questionType === QuestionType.SUBMIT;

  // Resolve the active variant for transition questions
  const activeVariant = isTransition
    ? ((question as TransitionQuestion).variants?.find((variant) =>
        variant.conditions.every((condition) =>
          answers.some((a) => {
            if (a.questionId !== condition.questionId) return false;
            if (Array.isArray(a.answerId)) {
              return a.answerId.includes(condition.answerId);
            }
            return a.answerId === condition.answerId;
          }),
        ),
      ) ?? null)
    : null;

  // Resolve the active info toast variant (if question has infoToastVariants)
  const activeInfoToastVariant =
    "infoToastVariants" in question && question.infoToastVariants
      ? (question.infoToastVariants.find((variant) =>
          variant.conditions.every((condition) =>
            answers.some((a) => {
              if (a.questionId !== condition.questionId) return false;
              if (Array.isArray(a.answerId)) {
                return a.answerId.includes(condition.answerId);
              }
              return a.answerId === condition.answerId;
            }),
          ),
        ) ?? null)
      : null;

  const willAutoAdvance =
    question.questionType === QuestionType.MULTIPLE_CHOICE &&
    !question.allowMultipleAnswers &&
    !currentAnswer;

  const handleAnswerChange = (questionId: string, answerId: AnswerValue) => {
    if (willAutoAdvance) {
      setIsAutoAdvancing(true);
    }
    setAnswer(questionId, answerId);
  };

  const handleBack = () => {
    goBack();
  };

  const variant = useVariant();
  const pageVariant = PAGE_VARIANT[variant];

  const handleSkip = question.skipToQuestionId
    ? () => {
        fireSkipEvent(
          question,
          question.skipToQuestionId!,
          visibleQuestions,
          pageVariant,
        );
        goToQuestion(question.skipToQuestionId!);
      }
    : undefined;

  const unitPreferenceMap = (answersRecord[UNIT_PREFERENCE_KEY] ??
    {}) as Record<string, UnitSystem>;

  const handleUnitPreferenceChange = useCallback(
    (key: string, unit: UnitSystem) => {
      const current = (answersRecord[UNIT_PREFERENCE_KEY] ?? {}) as Record<
        string,
        UnitSystem
      >;
      setAnswer(UNIT_PREFERENCE_KEY, { ...current, [key]: unit });
    },
    [answersRecord, setAnswer],
  );

  const flattenText = (node: ReactNode): string => {
    if (typeof node === "string" || typeof node === "number") {
      return String(node);
    }
    if (Array.isArray(node)) {
      return node.map((child) => flattenText(child)).join("");
    }
    return "";
  };

  const sourceRichElements = {
    sup: (chunks: ReactNode) => <sup>{chunks}</sup>,
    a: (chunks: ReactNode) => {
      const href = flattenText(chunks).trim();
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {chunks}
        </a>
      );
    },
  };

  const infoToast =
    "infoToastTranslationKey" in question &&
    question.infoToastTranslationKey ? (
      <InfoToast
        title={tQuestionnaire("why-are-we-asking")}
        content={t.rich(
          activeInfoToastVariant?.infoToastTranslationKey ??
            question.infoToastTranslationKey,
          {
            sup: (chunks) => <sup>{chunks}</sup>,
          },
        )}
        source={
          activeInfoToastVariant?.infoToastSourceKey
            ? t.rich(
                activeInfoToastVariant.infoToastSourceKey,
                sourceRichElements,
              )
            : "infoToastSourceKey" in question && question.infoToastSourceKey
              ? t.rich(question.infoToastSourceKey, sourceRichElements)
              : undefined
        }
      />
    ) : null;

  const renderQuestion = () => {
    switch (question.questionType) {
      case QuestionType.MULTIPLE_CHOICE: {
        const visibleAnswers = question.answers.filter((a) =>
          isConditionMet(a.conditionalOn, answersRecord),
        );
        const filteredQuestion: MultipleChoiceQuestionType = {
          ...question,
          answers: visibleAnswers,
        };
        const visibleIds = new Set(visibleAnswers.map((a) => a.id));
        const selectedAnswer = getMultipleChoiceAnswer(currentAnswer);
        // Reconcile multi-select only; no single-select questions currently use answer-level conditionalOn.
        const reconciledAnswer = Array.isArray(selectedAnswer)
          ? selectedAnswer.filter((id) => visibleIds.has(id))
          : selectedAnswer;
        return (
          <MultipleChoiceQuestion
            key={question.questionId}
            question={filteredQuestion}
            selectedAnswer={reconciledAnswer}
            onAnswerChange={handleAnswerChange}
            onAutoAdvance={willAutoAdvance ? handleNext : undefined}
            infoToast={infoToast}
          />
        );
      }

      case QuestionType.TEXT:
        return (
          <TextInput
            key={question.questionId}
            question={question as TextQuestion}
            values={getTextAnswer(currentAnswer)}
            onChange={setAnswer}
            onValidationChange={onValidationChange}
            unitPreferenceMap={unitPreferenceMap}
            onUnitPreferenceChange={handleUnitPreferenceChange}
            infoToast={infoToast}
          />
        );

      case QuestionType.DATE_PICKER:
        return (
          <DatePickerQuestion
            key={question.questionId}
            question={question}
            value={getStringAnswer(currentAnswer) || null}
            onChange={(questionId, value) => setAnswer(questionId, value ?? "")}
            infoToast={infoToast}
          />
        );

      case QuestionType.TRANSITION: {
        const transition = question as TransitionQuestion;
        const source = activeVariant ?? transition;
        return (
          <TransitionContent
            key={question.questionId}
            questionTranslationKey={source.questionTranslationKey}
            overlineTranslationKey={source.overlineTranslationKey}
            contentBlocks={source.contentBlocks}
            theme={activeVariant?.variantKey ?? "default"}
          />
        );
      }
      case QuestionType.SUBMIT:
        return <SubmitQuestion key={question.questionId} question={question} />;

      default:
        return null;
    }
  };

  return (
    <>
      {!isTransition && (
        <ProgressBar
          totalQuestions={answerableTotal}
          currentQuestionIndex={isSubmit ? answerableTotal : answerableIndex}
        />
      )}
      <div
        key={currentIndex}
        className={`questionnaire-content questionnaire-content--slide-${slideDirection}${isFadingOut ? " questionnaire-content--fade-out" : ""}`}
      >
        {renderQuestion()}
      </div>
      <NavigationButtons
        canGoNext={canGoNext}
        canGoBack={canGoBack}
        isLastQuestion={isLastQuestion}
        isAutoAdvancing={isAutoAdvancing}
        goNext={handleNext}
        goBack={handleBack}
        onSkip={handleSkip}
        nextLabelKey={question.nextLabelKey}
      />
    </>
  );
}

export default QuestionRenderer;